import { canUseCart } from './comensalRole';
import type { Comensal } from './api/split-payment';
import {
  COMENSAL_ACCESS_CHANGED_EVENT,
  COMENSAL_ACCESS_SNAPSHOT_KEY,
  COMENSAL_DEVICE_TOKEN_KEY,
  SPLIT_PAYMENT_AVAILABLE_KEY,
  TABLE_SESSION_ENTRY_KEY,
} from './sessionStorageKeys';
import { extractTableNumberFromPath } from './tableContext';

export interface ComensalAccessSnapshot {
  sessionToken: string;
  role: string;
  joinStatus: string;
  hasSplitLedger: boolean;
}

function emitAccessChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(COMENSAL_ACCESS_CHANGED_EVENT));
  }
}

export function persistComensalAccess(comensal: Comensal, sessionToken: string): void {
  if (typeof window === 'undefined') return;

  window.sessionStorage.setItem(
    COMENSAL_ACCESS_SNAPSHOT_KEY,
    JSON.stringify({
      sessionToken,
      role: comensal.role,
      joinStatus: comensal.join_status,
      hasSplitLedger: comensal.session?.has_split_ledger === true,
    }),
  );
  emitAccessChanged();
}

export function clearComensalAccess(): void {
  if (typeof window === 'undefined') return;

  if (window.sessionStorage.getItem(COMENSAL_ACCESS_SNAPSHOT_KEY) === null) return;

  window.sessionStorage.removeItem(COMENSAL_ACCESS_SNAPSHOT_KEY);
  emitAccessChanged();
}

export function setSplitPaymentAvailable(available: boolean | null): void {
  if (typeof window === 'undefined') return;

  if (available === null) {
    window.sessionStorage.removeItem(SPLIT_PAYMENT_AVAILABLE_KEY);
  } else {
    window.sessionStorage.setItem(SPLIT_PAYMENT_AVAILABLE_KEY, available ? 'true' : 'false');
  }
  emitAccessChanged();
}

/** 서버가 이 브라우저의 현재 세션에 저장한 분할 가용성. null은 아직 모르는 상태다. */
export function readSplitPaymentAvailable(): boolean | null {
  if (typeof window === 'undefined') return null;

  const value = window.sessionStorage.getItem(SPLIT_PAYMENT_AVAILABLE_KEY);
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
}

export function readComensalAccess(): ComensalAccessSnapshot | null {
  if (typeof window === 'undefined') return null;

  const raw = window.sessionStorage.getItem(COMENSAL_ACCESS_SNAPSHOT_KEY);
  if (raw === null) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<ComensalAccessSnapshot>;
    return typeof parsed.sessionToken === 'string'
      && typeof parsed.role === 'string'
      && typeof parsed.joinStatus === 'string'
      ? {
          sessionToken: parsed.sessionToken,
          role: parsed.role,
          joinStatus: parsed.joinStatus,
          hasSplitLedger: parsed.hasSplitLedger === true,
        }
      : null;
  } catch {
    return null;
  }
}

/**
 * D2 장바구니/주문 게이트.
 *
 * 분할이 꺼졌거나 아직 가용성을 모르면 기존 매장의 흐름을 보존한다. 서버가 분할을
 * 명시적으로 켰다면 신원이 아직 로드되지 않은 짧은 구간도 차단한다. 그렇지 않으면
 * 새로고침 직후 한 번의 클릭으로 Invitado가 주문을 만들 수 있다.
 */
export type ComensalOrderBlockReason = 'join_required' | 'split_disabled_with_ledger';

export function currentComensalOrderBlockReason(
  options: { forceTableOrder?: boolean } = {},
): ComensalOrderBlockReason | null {
  if (typeof window === 'undefined') return null;

  // CartProvider는 앱 전체에 살아 있다. 테이블 세션을 남긴 채 pickup/view 화면으로
  // 이동했다고 그 장바구니까지 좌석 합류 규칙으로 막으면 안 된다. API에서 명시적으로
  // table 주문을 만드는 경우만 forceTableOrder로 이 경계를 우회해 서버 전 차단을 유지한다.
  if (!options.forceTableOrder) {
    const isTablePath = extractTableNumberFromPath(window.location.pathname) !== undefined;
    const hasTableAccess = Boolean(window.sessionStorage.getItem('table_session_token'))
      || Boolean(window.sessionStorage.getItem(TABLE_SESSION_ENTRY_KEY));
    if (!isTablePath || !hasTableAccess) return null;
  }

  const access = readComensalAccess();
  const currentSessionToken = window.sessionStorage.getItem('table_session_token');
  if (window.sessionStorage.getItem(SPLIT_PAYMENT_AVAILABLE_KEY) !== 'true') {
    return access !== null
      && access.sessionToken === currentSessionToken
      && access.hasSplitLedger
      ? 'split_disabled_with_ledger'
      : null;
  }

  if (!window.sessionStorage.getItem(COMENSAL_DEVICE_TOKEN_KEY)) {
    return 'join_required';
  }

  return access !== null
    && access.sessionToken === currentSessionToken
    && access.joinStatus === 'joined'
    && canUseCart(access.role)
    ? null
    : 'join_required';
}

export function canCurrentComensalOrder(options: { forceTableOrder?: boolean } = {}): boolean {
  return currentComensalOrderBlockReason(options) === null;
}

export function currentComensalHeaders(): Record<string, string> | undefined {
  if (typeof window === 'undefined') return undefined;

  const token = window.sessionStorage.getItem(COMENSAL_DEVICE_TOKEN_KEY);
  return token ? { 'X-Comensal-Token': token } : undefined;
}
